import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import { Avatar, IconButton, Tooltip, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import React, { useEffect } from "react";
import { useAllUsers } from "../../common/contexts/allUsersContext";
import { useUser } from "../../common/contexts/userContext";
import { palette } from "../../styles/palette";
import { RankingData } from "../../types/RankingData";
import { truncateString } from "../../utils/textUtils";
import BetOutcomeIcon from "../components/BetOutcomeIcon";
import "../MobileApp.scss";
import "./MobileRanking.scss";
import UserBetsModal from "./UserBetsModal";

interface MobileRankingTableProps {
  leagueRankingData: RankingData[];
  isDesktop?: boolean;
}

const MobileRankingTable: React.FC<MobileRankingTableProps> = ({
  leagueRankingData,
  isDesktop,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedUser, setSelectedUser] = React.useState<{
    id: string;
    username: string;
  } | null>(null);
  const { users } = useAllUsers();
  const { user } = useUser();
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const columns: Column[] = [
    { id: "position", label: "", minWidth: 0, align: "right" },
    { id: "username", label: "Player", minWidth: isDesktop ? 40 : 0 },
    { id: "points", label: "Points", minWidth: 15 },
    {
      id: "perfectBets",
      label: isDesktop ? "Perfect Bets" : "PB",
      minWidth: 15,
      // align: "right",
      format: (value: number) => value.toLocaleString("en-US"),
    },
    {
      id: "goodBets",
      label: isDesktop ? "Good Bets" : "GB",
      minWidth: 15,
      // align: "right",
      format: (value: number) => value.toLocaleString("en-US"),
    },
    {
      id: "wrongBets",
      label: isDesktop ? "Wrong Bets" : "WB",
      minWidth: 15,
      // align: "right",
      format: (value: number) => value.toLocaleString("en-US"),
    },
  ];

  useEffect(() => {
    setPage(0);
  }, [leagueRankingData]);

  const headCellClassName = `rankingTable__headCell${
    isDesktop ? " rankingTable__headCell--desktop" : ""
  }`;

  const sortedRows = [...(leagueRankingData ?? [])].sort(
    (a, b) => a.rankPlace - b.rankPlace,
  );
  const pageRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  const userRow = sortedRows.find((row) => row.userId === user.id);
  const userOnPage = pageRows.some((row) => row.userId === user.id);

  const renderRow = (row: RankingData, pinned = false) => {
    const isUser = row.userId === user.id;
    const lastFiveBets =
      users.find((item) => item.id === row.userId)?.lastFiveBets ?? [];
    const lastFiveSlots = Array.from({ length: 5 }, (_, index) =>
      index < lastFiveBets.length ? lastFiveBets[index] : null
    );

    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={pinned ? "ranking-pinned-user" : `row-ranking-${row.userId}`}
        className={
          pinned
            ? "rankingTable__pinnedRow"
            : isUser
            ? "rankingTable__userRow"
            : undefined
        }
      >
        <TableCell
          style={{
            color: palette.textMuted,
            textAlign: isDesktop ? "right" : undefined,
            fontWeight: isUser ? "600 " : undefined,
          }}
        >
          {" "}
          {row.rankPlace === 1 ? <FirstPlace /> : `${row.rankPlace}.`}
        </TableCell>
        <TableCell className="rankingTable__cellMuted">
          {" "}
          <Typography
            component="span"
            noWrap
            sx={{
              wordWrap: "break-word",
              fontSize: "14px",
              fontWeight: isUser ? "600" : undefined,
            }}
            style={
              isDesktop
                ? {
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }
                : undefined
            }
          >
            {isDesktop && (
              <Avatar src={row.avatarUrl} className="rankingTable__avatar" />
            )}
            {isDesktop
              ? truncateString(25, row.username)
              : truncateString(9, row.username)}
          </Typography>
        </TableCell>
        <TableCell
          style={{
            color: palette.textMuted,
            fontWeight: isUser ? "600" : undefined,
          }}
        >
          {" "}
          {row.points}
        </TableCell>
        <TableCell
          style={{
            color: palette.textMuted,
            fontWeight: isUser ? "600" : undefined,
          }}
        >
          {" "}
          {row.perfectBets}
        </TableCell>
        <TableCell
          style={{
            color: palette.textMuted,
            fontWeight: isUser ? "600" : undefined,
          }}
        >
          {" "}
          {row.correctBets}
        </TableCell>
        <TableCell
          style={{
            color: palette.textMuted,
            fontWeight: isUser ? "600" : undefined,
          }}
        >
          {" "}
          {row.wrongBets}
        </TableCell>
        {isDesktop && (
          <TableCell className="rankingTable__cellMuted">
            <div className="rankingTable__lastFive" aria-label="Last 5 bets">
              {lastFiveSlots.map((item, index) =>
                item === null ? (
                  <span
                    key={`${row.userId}-last-five-empty-${index}`}
                    className="rankingTable__lastFivePlaceholder"
                    aria-hidden="true"
                  />
                ) : (
                  <BetOutcomeIcon
                    outcome={item}
                    key={`${row.userId}-last-five-${index}`}
                  />
                )
              )}
            </div>
          </TableCell>
        )}
        <TableCell
          className="rankingTable__cellMuted"
          padding="checkbox"
          align="center"
        >
          <Tooltip title="View results">
            <IconButton
              size="small"
              aria-label={`View ${row.username}'s results`}
              onClick={() =>
                setSelectedUser({
                  id: row.userId,
                  username: row.username,
                })
              }
            >
              <FormatListBulletedOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Paper
        sx={{ width: "93%", overflowY: "auto", background: palette.bgSurface }}
      >
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={headCellClassName}
                    align={
                      isDesktop
                        ? undefined
                        : (column.align as
                            | "center"
                            | "left"
                            | "right"
                            | "inherit"
                            | "justify"
                            | undefined)
                    }
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {isDesktop && (
                  <TableCell className={headCellClassName}>
                    Last 5 bets
                  </TableCell>
                )}
                <TableCell
                  className={headCellClassName}
                  align="center"
                  aria-label="Results"
                >
                  Bets
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => renderRow(row))}
              {/* Pin the current user's row when they're not on the visible page. */}
              {userRow && !userOnPage && renderRow(userRow, true)}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={leagueRankingData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className={`table-bottom rankingTable__pagination${
            isDesktop ? "" : " rankingTable__pagination--mobile"
          }`}
        />
      </Paper>
      <UserBetsModal
        open={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        userId={selectedUser?.id ?? null}
        username={selectedUser?.username ?? ""}
      />
    </>
  );
};

export default MobileRankingTable;

interface Column {
  id:
    | "username"
    | "points"
    | "perfectBets"
    | "goodBets"
    | "wrongBets"
    | "position";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

const FirstPlace = () => {
  return (
    <div className="rankingTable__firstPlace">
      <img
        src="./images/crown_trans.png"
        height={24}
        className="rankingTable__crown"
        alt="crown"
      />
      {1}.
    </div>
  );
};
